// Horizontal summary bar shown above the forecast calendar(s).
//
// Used in two places:
//   1. Admin.jsx  — above all selected clinic blocks, with a dynamic clinicCount (e.g. "Total for 4 clinics")
//   2. Forecasting.jsx — above the single-clinic standalone calendar, always clinicCount=1
//
// For past months (isPastMonth=true) only MTD Production is shown — Forecast to Month End
// and Month-End are hidden because completed months have no remaining forecast to display.
import { formatMoney } from "@utils/format";

// Thin vertical rule between stat columns
function Divider() {
  return <div className="w-px bg-palette-gray-divider self-stretch" />;
}

// A single labelled stat column (label above, value below)
function StatColumn({
  label,
  value,
  valueClass = "text-base font-semibold text-palette-gray-primary",
}) {
  return (
    <div>
      <p className="text-xs text-palette-gray-secondary uppercase tracking-wide mb-0.5">{label}</p>
      <p className={valueClass}>{value}</p>
    </div>
  );
}

export default function ForecastingSummaryBar({
  mtd,
  forecastFromToday,
  monthEnd,
  clinicCount,
  isPastMonth,
}) {
  return (
    <div className="flex items-center gap-10 px-5 py-3 bg-palette-gray-soft rounded-lg border border-palette-gray-subtle">
      {/* How many clinics are included in these combined totals */}
      <p className="text-sm font-medium text-palette-gray-secondary whitespace-nowrap">
        Total for {clinicCount} {clinicCount === 1 ? "clinic" : "clinics"}
      </p>

      <Divider />

      {/* MTD Production — always visible; for past months this is the full completed-month total */}
      <StatColumn label="MTD Production" value={formatMoney(mtd)} />

      {/* Forecast to Month End and Month-End are only relevant for the current in-progress month */}
      {!isPastMonth && (
        <>
          <Divider />
          <StatColumn label="Forecast to Month End" value={formatMoney(forecastFromToday)} />
          <Divider />
          {/* Month-End is highlighted in indigo — it is the key projected end-of-month number */}
          <StatColumn
            label="Month-End"
            value={formatMoney(monthEnd)}
            valueClass="text-base font-semibold text-palette-indigo-badge"
          />
        </>
      )}
    </div>
  );
}
